import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Add from '@mui/icons-material/Add';
import { Tabs, Tab } from '@mui/material';
import { useModal } from 'src/hooks';
import DataTable, {
  DataTableWrapper,
  DataTableWrapperActionButton,
  getActionsColConfig,
  useTableQueryParams,
} from 'src/components/DataTable';
import { isModalComplete } from 'src/components/ModalDialog';
import { useStoreUnitActions } from 'src/redux/helpers';
import PageCardLayout from 'src/components/PageCardLayout';
import { useUserRoles } from 'src/auth';
import { useSolutionUserRoles } from '../hooks';
import solutionsStore, { useSolutionGroups, useSolutionUsers } from '../redux';
import {
  SolutionGroupItemField,
  SolutionType,
  SolutionUserItemField,
  SolutionUsersType,
} from '../types';
import SolutionUsersAction from './components/SolutionUsersAction';
import SolutionUsersRole from './components/SolutionUsersRole';
import SolutionUsersAssignModal from './components/SolutionUsersAssignModal';
import SolutionUsersPhoneNumber from './components/SolutionUsersPhoneNumber';
import SolutionGroupsAssignModal from './components/SolutionGroupsAssignModal';
import SolutionGroupsAction from './components/SolutionGroupsAction';
import SolutionUserGroup from './components/SolutionUserGroup';

const columns = [
  {
    id: SolutionUserItemField.Email,
    title: 'Email address',
  },
  {
    id: SolutionUserItemField.Role,
    title: 'Role',
    Component: SolutionUsersRole,
  },
  {
    id: SolutionUserItemField.PhoneNumber,
    title: 'Phone Number',
    Component: SolutionUsersPhoneNumber,
  },
  {
    id: SolutionUserItemField.NotificationPreferList,
    title: 'Notification preference',
  },
  {
    id: SolutionUserItemField.GroupIds,
    title: 'Groups',
    Component: SolutionUserGroup,
  },
  getActionsColConfig(SolutionUsersAction),
];

const groupColumns = [
  {
    id: SolutionGroupItemField.Name,
    title: 'Group Name',
  },
  {
    id: SolutionGroupItemField.Description,
    title: 'Description',
  },
  {
    id: SolutionGroupItemField.CreatedBy,
    title: 'Created By',
  },
  getActionsColConfig(SolutionGroupsAction),
];

export default function SolutionUsers() {
  const { solutionId, solutionType } = useParams();
  const actions = useStoreUnitActions(solutionsStore);
  const { isSolutionEditor } = useSolutionUserRoles(solutionId);
  const { isPlatformReader } = useUserRoles();
  const [data, loading] = useSolutionUsers();
  const [groupData, dataLoading] = useSolutionGroups();
  const { paging } = useTableQueryParams();
  const [type, setType] = useState(SolutionUsersType.Users);

  useEffect(() => {
    actions.sites(solutionId);
    actions.groups(solutionId);
  }, [solutionId]);

  const handleChange = (event: any, newValue) => {
    setType(newValue);
  };

  const [AssignNewUserDialog, showAssignNewUserDialog] = useModal(
    SolutionUsersAssignModal,
    {
      onClose: (event, reason) =>
        isModalComplete(reason) && actions.users(solutionId),
    }
  );

  const [CreateNewGroupDialog, showCreateNewGroupDialog] = useModal(
    SolutionGroupsAssignModal,
    {
      onClose: (event, reason) =>
        isModalComplete(reason) && actions.groups(solutionId),
    }
  );

  const updatedData = data?.map((item) => {
    if (item.notificationPreference) {
      const notificationPreference: number[] = [];
      const notificationPreferListArray = item.notificationPreference.map(
        (notificationPrefer) => {
          if (notificationPrefer === 'email') {
            notificationPreference.push(1);
            return 'Email';
          }
          if (notificationPrefer === 'sms') {
            notificationPreference.push(2);
            return 'SMS';
          }
          notificationPreference.push(3);
          return 'Whatsapp';
        }
      );

      const notificationPreferList =
        notificationPreferListArray.length > 0
          ? notificationPreferListArray.toString()
          : '–';
      return {
        ...item,
        notificationPreferList: [notificationPreferList],
        notificationPreference,
        phoneNumber: item.phoneNumber ? item.phoneNumber : '–',
      };
    }
    return item;
  });

  useEffect(() => {
    actions.users(solutionId);
    actions.groups(solutionId);
  }, [solutionId]);

  return (
    <PageCardLayout>
      <Tabs
        value={type}
        onChange={handleChange}
        aria-label="wrapped label tabs example"
        sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 3 }}
      >
        <Tab value={SolutionUsersType.Users} label="Users" />
        <Tab value={SolutionUsersType.Groups} label="Groups" />
      </Tabs>
      {type === SolutionUsersType.Users && (
        <DataTableWrapper
          title="Users"
          url="/help-center/docs/IndustrialSetup/UserOnboarding/UserOnboardingProcess/"
          showUrl={solutionType === SolutionType.Industrial}
          infoTitle="Help center"
          actionsPanel={
            <DataTableWrapperActionButton
              onClick={showAssignNewUserDialog}
              startIcon={<Add />}
              disabled={!isSolutionEditor}
            >
              Assign user
            </DataTableWrapperActionButton>
          }
        >
          <DataTable
            sx={{ minHeight: 0 }}
            data={updatedData}
            columns={columns}
            loading={loading}
            {...paging}
          />
          {AssignNewUserDialog}
        </DataTableWrapper>
      )}
      {type === SolutionUsersType.Groups && (
        <DataTableWrapper
          title="Groups"
          actionsPanel={
            <DataTableWrapperActionButton
              onClick={showCreateNewGroupDialog}
              startIcon={<Add />}
              disabled={isPlatformReader}
            >
              Create New
            </DataTableWrapperActionButton>
          }
        >
          <DataTable
            sx={{ minHeight: 0 }}
            data={groupData}
            columns={groupColumns}
            loading={dataLoading}
            {...paging}
          />
          {CreateNewGroupDialog}
        </DataTableWrapper>
      )}
    </PageCardLayout>
  );
}
